import { useCallback, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { Conversation } from '@11labs/client'
import {
  Bot, Phone, BookOpen, Wrench, Search, ChevronRight,
  Save, Loader2, Trash2, Plus, X, Check, ChevronDown, ChevronUp,
  Settings, Mic, MicOff, User, Headphones, Activity, Smartphone,
  MessageSquare, Send, ClipboardList, AlertTriangle,
} from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import {
  usePlatformAdminAgents,
  usePlatformAdminAgent,
  usePlatformAdminPhoneNumbers,
  useAdminUpdatePrompt,
  useAdminUpdateTools,
  useAdminUpdateKnowledgeBase,
  useAdminAssignPhone,
  useAdminRestaurantAgents,
  useAdminGlobalConfig,
  useAdminUpdateGlobalConfig,
  useAdminAgentDebugLogs,
  useAdminClearAgentDebugLogs,
  useAdminOrderMonitor,
  useAdminSunmiDiagnostics,
  useAdminClearSunmiDiagnostics,
  useAdminTwilioSmsDiagnostics,
  useAdminClearTwilioSmsDiagnostics,
  useAdminReadyTimeMessaging,
  useAdminUpdateReadyTimeMessaging,
  useAdminSendReadyTimeSmsTest,
  type AdminAgent,
  type AdminAgentTool,
  type AdminAgentKbDoc,
  type AdminAgentDebugLog,
  type OrderMonitorRow,
  type OrderMonitorRestaurant,
  type OrderMonitorStage,
  type OrderMonitorStageStatus,
  type SunmiDiagnosticLog,
  type TwilioSmsDiagnosticLog,
} from '@/lib/queries/usePlatformAdmin'
import { storage } from '@/utils/storage'
import { STRAPI_BASE_URL as STRAPI_URL } from '@/config/api'

// ── Global Config Panel ────────────────────────────────────────

function GlobalConfigPanel() {
  const { data: config, isLoading } = useAdminGlobalConfig()
  const [promptTemplate, setPromptTemplate] = useState('')
  const update = useAdminUpdateGlobalConfig()

  useEffect(() => {
    if (config) setPromptTemplate(config.promptTemplate ?? '')
  }, [config])

  async function save() {
    try {
      await update.mutateAsync({ promptTemplate })
      toast.success('Global config saved')
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to save')
    }
  }

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[var(--color-text-muted)]" /></div>

  return (
    <div className="flex flex-col h-full p-5 gap-4">
      <div>
        <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Global Agent Config</h2>
        <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
          Prompt template applied to new agents on provision. Supports{' '}
          <code className="text-purple-400">{'{restaurant_name}'}</code>,{' '}
          <code className="text-purple-400">{'{restaurant_slug}'}</code>,{' '}
          <code className="text-purple-400">{'{phone}'}</code>,{' '}
          <code className="text-purple-400">{'{timezone}'}</code>,{' '}
          <code className="text-purple-400">{'{current_date}'}</code>, and{' '}
          <code className="text-purple-400">{'{current_time}'}</code>.{' '}
          Use <code className="text-purple-400">{'{default_prompt}'}</code> to include the built-in example prompt.{' '}
          Leave empty to use the built-in default.
        </p>
      </div>
      <div className="flex-1 flex flex-col gap-2">
        <label className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide">Prompt Template</label>
        <textarea
          value={promptTemplate}
          onChange={(e) => setPromptTemplate(e.target.value)}
          placeholder="Leave empty to use built-in default prompt…"
          className="flex-1 w-full p-3 text-sm font-mono rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] focus:outline-none focus:border-purple-500/60 resize-none"
          style={{ minHeight: 320 }}
        />
      </div>
      {config && config.tools.length > 0 && (
        <div>
          <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-1.5">Global Tools ({config.tools.length})</p>
          <div className="flex flex-wrap gap-1.5">
            {config.tools.map((t: AdminAgentTool, i: number) => (
              <span key={i} className="px-2 py-1 rounded text-xs bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-muted)]">
                {t.name}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between pt-2 border-t border-[var(--color-border-subtle)]">
        <p className="text-xs text-amber-400/80">Changes apply to new agents and manual syncs only.</p>
        <Button
          onClick={save}
          disabled={update.isPending || promptTemplate === (config?.promptTemplate ?? '')}
          className="bg-purple-600 hover:bg-purple-500 text-white"
        >
          {update.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save
        </Button>
      </div>
    </div>
  )
}

// ── Agent List ─────────────────────────────────────────────────

function AgentList({
  agents,
  saAgentIds,
  restaurantAgents,
  selectedId,
  onSelect,
  onSelectGlobalConfig,
  onSelectOrderMonitor,
  onSelectSunmiDiagnosis,
  onSelectTwilioSms,
  showGlobalConfig,
  showOrderMonitor,
  showSunmiDiagnosis,
  showTwilioSms,
  phoneNumbers,
}: {
  agents: AdminAgent[]
  saAgentIds: Set<string>
  restaurantAgents: { agentId: string; name: string; phone: string | null }[]
  selectedId: string | null
  onSelect: (id: string) => void
  onSelectGlobalConfig: () => void
  onSelectOrderMonitor: () => void
  onSelectSunmiDiagnosis: () => void
  onSelectTwilioSms: () => void
  showGlobalConfig: boolean
  showOrderMonitor: boolean
  showSunmiDiagnosis: boolean
  showTwilioSms: boolean
  phoneNumbers: { agent_id: string; phone_number: string }[]
}) {
  const [search, setSearch] = useState('')
  const [saOpen, setSaOpen] = useState(true)
  const [otherOpen, setOtherOpen] = useState(true)

  const q = search.toLowerCase()
  const saAgents = agents.filter((a) => saAgentIds.has(a.agent_id) && a.name.toLowerCase().includes(q))
  const otherAgents = agents.filter((a) => !saAgentIds.has(a.agent_id) && a.name.toLowerCase().includes(q))

  function AgentRow({ agent }: { agent: AdminAgent }) {
    const phone = phoneNumbers.find((p) => p.agent_id === agent.agent_id)
    const raInfo = restaurantAgents.find((r) => r.agentId === agent.agent_id)
    const isSelected = selectedId === agent.agent_id
    return (
      <button
        onClick={() => onSelect(agent.agent_id)}
        className={`w-full text-left px-3 py-2.5 border-b border-[var(--color-border-subtle)] hover:bg-[var(--color-bg-elevated)] transition-colors flex items-center justify-between gap-2 ${
          isSelected ? 'bg-purple-500/8 border-l-2 border-l-purple-500' : ''
        }`}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">
              {raInfo?.name ?? agent.name}
            </p>
            {saAgentIds.has(agent.agent_id) && (
              <span className="shrink-0 px-1 py-0.5 rounded text-[0.55rem] font-bold uppercase tracking-wide bg-purple-500/20 text-purple-400">SA</span>
            )}
          </div>
          {phone && (
            <p className="text-xs text-purple-400 mt-0.5">{phone.phone_number}</p>
          )}
        </div>
        <ChevronRight className="h-3.5 w-3.5 text-[var(--color-text-muted)] shrink-0" />
      </button>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Global Config button */}
      <button
        onClick={onSelectGlobalConfig}
        className={`w-full text-left px-3 py-2.5 border-b border-[var(--color-border-subtle)] hover:bg-[var(--color-bg-elevated)] transition-colors flex items-center gap-2 ${
          showGlobalConfig ? 'bg-purple-500/8 border-l-2 border-l-purple-500' : ''
        }`}
      >
        <Settings className="h-3.5 w-3.5 text-purple-400 shrink-0" />
        <span className="text-sm font-medium text-[var(--color-text-primary)]">Global Config</span>
      </button>
      <button
        onClick={onSelectOrderMonitor}
        className={`w-full text-left px-3 py-2.5 border-b border-[var(--color-border-subtle)] hover:bg-[var(--color-bg-elevated)] transition-colors flex items-center gap-2 ${
          showOrderMonitor ? 'bg-purple-500/8 border-l-2 border-l-purple-500' : ''
        }`}
      >
        <ClipboardList className="h-3.5 w-3.5 text-purple-400 shrink-0" />
        <span className="text-sm font-medium text-[var(--color-text-primary)]">Order Monitor</span>
      </button>
      <button
        onClick={onSelectSunmiDiagnosis}
        className={`w-full text-left px-3 py-2.5 border-b border-[var(--color-border-subtle)] hover:bg-[var(--color-bg-elevated)] transition-colors flex items-center gap-2 ${
          showSunmiDiagnosis ? 'bg-purple-500/8 border-l-2 border-l-purple-500' : ''
        }`}
      >
        <Smartphone className="h-3.5 w-3.5 text-purple-400 shrink-0" />
        <span className="text-sm font-medium text-[var(--color-text-primary)]">Sunmi Diagnosis</span>
      </button>
      <button
        onClick={onSelectTwilioSms}
        className={`w-full text-left px-3 py-2.5 border-b border-[var(--color-border-subtle)] hover:bg-[var(--color-bg-elevated)] transition-colors flex items-center gap-2 ${
          showTwilioSms ? 'bg-purple-500/8 border-l-2 border-l-purple-500' : ''
        }`}
      >
        <MessageSquare className="h-3.5 w-3.5 text-purple-400 shrink-0" />
        <span className="text-sm font-medium text-[var(--color-text-primary)]">Twilio SMS</span>
      </button>

      {/* Search */}
      <div className="p-3 border-b border-[var(--color-border-subtle)]">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--color-text-muted)]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search agents…"
            className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-purple-500/60"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* SheAnswers Agents */}
        <button
          onClick={() => setSaOpen((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2 bg-[var(--color-bg-subtle)] border-b border-[var(--color-border-subtle)] text-xs font-semibold uppercase tracking-wide text-purple-400"
        >
          <span>SheAnswers Agents ({saAgents.length})</span>
          {saOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
        {saOpen && saAgents.map((agent) => <AgentRow key={agent.agent_id} agent={agent} />)}
        {saOpen && saAgents.length === 0 && (
          <p className="text-xs text-[var(--color-text-muted)] text-center py-4">No SheAnswers agents.</p>
        )}

        {/* Other Agents */}
        {otherAgents.length > 0 && (
          <>
            <button
              onClick={() => setOtherOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2 bg-[var(--color-bg-subtle)] border-b border-[var(--color-border-subtle)] text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]"
            >
              <span>Other Agents ({otherAgents.length})</span>
              {otherOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            {otherOpen && otherAgents.map((agent) => <AgentRow key={agent.agent_id} agent={agent} />)}
          </>
        )}
      </div>
    </div>
  )
}

// ── Prompt Tab ─────────────────────────────────────────────────

function PromptTab({ agentId, prompt }: { agentId: string; prompt: string }) {
  const [value, setValue] = useState(prompt)
  const update = useAdminUpdatePrompt(agentId)

  async function save() {
    try {
      await update.mutateAsync(value)
      toast.success('Prompt saved')
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to save prompt')
    }
  }

  return (
    <div className="flex flex-col gap-3 h-full">
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="flex-1 w-full p-3 text-sm font-mono rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] focus:outline-none focus:border-purple-500/60 resize-none"
        style={{ minHeight: 400 }}
      />
      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={update.isPending || value === prompt}
          className="bg-purple-600 hover:bg-purple-500 text-white"
        >
          {update.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Prompt
        </Button>
      </div>
    </div>
  )
}

// ── Tools Tab ──────────────────────────────────────────────────

function ToolsTab({ agentId, tools }: { agentId: string; tools: AdminAgentTool[] }) {
  const [localTools, setLocalTools] = useState<AdminAgentTool[]>(tools)
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [newTool, setNewTool] = useState({ name: '', description: '', url: '', method: 'GET' as 'GET' | 'POST' })
  const update = useAdminUpdateTools(agentId)

  async function save(updated: AdminAgentTool[]) {
    try {
      await update.mutateAsync(updated)
      toast.success('Tools saved')
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to save tools')
    }
  }

  function removeTool(idx: number) {
    const updated = localTools.filter((_, i) => i !== idx)
    setLocalTools(updated)
    save(updated)
  }

  function addTool() {
    if (!newTool.name || !newTool.url) { toast.error('Name and URL are required'); return }
    const tool: AdminAgentTool = {
      type: 'webhook',
      name: newTool.name,
      description: newTool.description,
      api_schema: {
        url: newTool.url,
        method: newTool.method,
        ...(newTool.method === 'GET'
          ? { query_params_schema: { properties: {}, required: [] } }
          : { request_body_schema: { type: 'object', properties: {}, required: [] } }),
      },
    }
    const updated = [...localTools, tool]
    setLocalTools(updated)
    save(updated)
    setNewTool({ name: '', description: '', url: '', method: 'GET' })
    setShowAdd(false)
  }

  function updateToolUrl(idx: number, url: string) {
    const updated = localTools.map((t, i) =>
      i === idx ? { ...t, api_schema: { ...t.api_schema, url } } : t,
    )
    setLocalTools(updated)
  }

  return (
    <div className="space-y-2">
      {localTools.map((tool, idx) => (
        <div key={idx} className="border border-[var(--color-border-subtle)] rounded-lg overflow-hidden">
          <button
            onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
            className="w-full flex items-center gap-3 p-3 hover:bg-[var(--color-bg-elevated)] transition-colors text-left"
          >
            <span className={`px-1.5 py-0.5 rounded text-[0.6rem] font-bold uppercase tracking-wide shrink-0 ${
              tool.api_schema.method === 'GET' ? 'bg-blue-500/15 text-blue-400' : 'bg-green-500/15 text-green-400'
            }`}>
              {tool.api_schema.method}
            </span>
            <span className="text-sm font-medium text-[var(--color-text-primary)] flex-1 truncate">{tool.name}</span>
            <button
              onClick={(e) => { e.stopPropagation(); removeTool(idx) }}
              className="p-1 rounded hover:bg-red-500/10 text-[var(--color-text-muted)] hover:text-red-400 transition-colors shrink-0"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </button>
          {expandedIdx === idx && (
            <div className="border-t border-[var(--color-border-subtle)] p-3 space-y-2 bg-[var(--color-bg-subtle)]">
              <p className="text-xs text-[var(--color-text-muted)]">{tool.description}</p>
              <div className="flex gap-2">
                <input
                  value={tool.api_schema.url}
                  onChange={(e) => updateToolUrl(idx, e.target.value)}
                  className="flex-1 px-2 py-1 text-xs font-mono rounded bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] focus:outline-none focus:border-purple-500/60"
                />
                <Button size="sm" variant="secondary" onClick={() => save(localTools)} disabled={update.isPending}>
                  <Save className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}

      {showAdd ? (
        <div className="border border-purple-500/30 rounded-lg p-3 space-y-2 bg-purple-500/5">
          <div className="grid grid-cols-2 gap-2">
            <input
              placeholder="Tool name"
              value={newTool.name}
              onChange={(e) => setNewTool((p) => ({ ...p, name: e.target.value }))}
              className="px-2 py-1.5 text-sm rounded bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] focus:outline-none focus:border-purple-500/60"
            />
            <select
              value={newTool.method}
              onChange={(e) => setNewTool((p) => ({ ...p, method: e.target.value as 'GET' | 'POST' }))}
              className="px-2 py-1.5 text-sm rounded bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] focus:outline-none"
            >
              <option>GET</option>
              <option>POST</option>
            </select>
          </div>
          <input
            placeholder="URL"
            value={newTool.url}
            onChange={(e) => setNewTool((p) => ({ ...p, url: e.target.value }))}
            className="w-full px-2 py-1.5 text-sm font-mono rounded bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] focus:outline-none focus:border-purple-500/60"
          />
          <input
            placeholder="Description"
            value={newTool.description}
            onChange={(e) => setNewTool((p) => ({ ...p, description: e.target.value }))}
            className="w-full px-2 py-1.5 text-sm rounded bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] focus:outline-none focus:border-purple-500/60"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" size="sm" onClick={() => setShowAdd(false)}><X className="h-3.5 w-3.5" /></Button>
            <Button size="sm" className="bg-purple-600 hover:bg-purple-500 text-white" onClick={addTool}>
              <Check className="h-3.5 w-3.5 mr-1" /> Add
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="secondary" size="sm" onClick={() => setShowAdd(true)}>
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Tool
        </Button>
      )}
    </div>
  )
}

// ── Knowledge Base Tab ─────────────────────────────────────────

function JsonBlock({ value }: { value: unknown }) {
  return (
    <pre className="max-h-80 overflow-auto rounded-md bg-black/30 border border-[var(--color-border-subtle)] p-3 text-xs leading-relaxed text-[var(--color-text-secondary)]">
      {JSON.stringify(value, null, 2)}
    </pre>
  )
}

function DebugLogCard({ log }: { log: AdminAgentDebugLog }) {
  const [expanded, setExpanded] = useState(false)
  const ok = log.status >= 200 && log.status < 300

  return (
    <div className="border border-[var(--color-border-subtle)] rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/[0.03]"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-xs font-semibold ${ok ? 'text-emerald-400' : 'text-red-400'}`}>{log.status}</span>
            <span className="text-sm font-semibold text-[var(--color-text-primary)]">{log.toolName}</span>
            <span className="text-xs text-[var(--color-text-muted)]">{log.source}</span>
            <span className="text-xs text-[var(--color-text-muted)]">{log.durationMs}ms</span>
          </div>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {new Date(log.timestamp).toLocaleString()} | {log.method} {log.path}
          </p>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
      </button>

      {expanded && (
        <div className="space-y-3 border-t border-[var(--color-border-subtle)] p-4">
          {log.error && <p className="text-sm text-red-400">{log.error}</p>}
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Request</p>
            <JsonBlock value={log.request} />
          </div>
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Response</p>
            <JsonBlock value={log.response} />
          </div>
        </div>
      )}
    </div>
  )
}

function DebugLogsTab({ agentId }: { agentId: string }) {
  const { data: logs = [], isLoading, refetch } = useAdminAgentDebugLogs(agentId)
  const clear = useAdminClearAgentDebugLogs(agentId)

  async function clearLogs() {
    try {
      await clear.mutateAsync()
      toast.success('Debug logs cleared')
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to clear logs')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Payload Debugger</h3>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            Captures public menu, order availability, and booking/order requests for this restaurant agent.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isLoading}>
            Refresh
          </Button>
          <Button variant="secondary" size="sm" onClick={clearLogs} disabled={clear.isPending || logs.length === 0}>
            Clear
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--color-text-muted)]" />
        </div>
      ) : logs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[var(--color-border-subtle)] p-6 text-sm text-[var(--color-text-muted)]">
          No captured payloads yet. Place a customer-page order or test the SheAnswers agent, then this tab will update.
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map((log) => (
            <DebugLogCard key={log.id} log={log} />
          ))}
        </div>
      )}
    </div>
  )
}

function statusClass(ok: boolean | null) {
  if (ok === true) return 'text-emerald-400'
  if (ok === false) return 'text-red-400'
  return 'text-[var(--color-text-muted)]'
}

function statusLabel(ok: boolean | null) {
  if (ok === true) return 'ok'
  if (ok === false) return 'fail'
  return 'info'
}

function shortId(value: string | null | undefined) {
  if (!value) return 'unknown'
  return value.length > 18 ? `${value.slice(0, 10)}...${value.slice(-6)}` : value
}

function stageClass(status: OrderMonitorStageStatus) {
  if (status === 'ok') return 'bg-emerald-500/15 border-emerald-500/35 text-emerald-300'
  if (status === 'fail') return 'bg-red-500/15 border-red-500/35 text-red-300'
  if (status === 'waiting') return 'bg-amber-500/15 border-amber-500/35 text-amber-300'
  if (status === 'skipped') return 'bg-sky-500/15 border-sky-500/35 text-sky-300'
  return 'bg-[var(--color-bg-elevated)] border-[var(--color-border-subtle)] text-[var(--color-text-muted)]'
}

function severityClass(severity: OrderMonitorRow['severity']) {
  if (severity === 'critical') return 'bg-red-500/15 border-red-500/35 text-red-300'
  if (severity === 'warning') return 'bg-amber-500/15 border-amber-500/35 text-amber-300'
  return 'bg-emerald-500/15 border-emerald-500/35 text-emerald-300'
}

function restaurantHealthClass(restaurant: OrderMonitorRestaurant, selected: boolean) {
  const selectedClass = selected ? 'ring-2 ring-purple-400/60' : ''
  if (restaurant.criticalOrders > 0) {
    return `border-red-500/45 bg-red-500/10 hover:bg-red-500/15 ${selectedClass}`
  }
  if (restaurant.warningOrders > 0 || restaurant.unseenOrders > 0) {
    return `border-amber-500/45 bg-amber-500/10 hover:bg-amber-500/15 ${selectedClass}`
  }
  if (restaurant.activeOrders > 0) {
    return `border-emerald-500/35 bg-emerald-500/10 hover:bg-emerald-500/15 ${selectedClass}`
  }
  return `border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] hover:bg-white/[0.03] ${selectedClass}`
}

function formatMonitorTimestamp(value: string | null | undefined) {
  if (!value) return 'No active orders'
  return new Date(value).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function RestaurantHeatmapCard({
  restaurant,
  selected,
  onSelect,
}: {
  restaurant: OrderMonitorRestaurant
  selected: boolean
  onSelect: () => void
}) {
  const name = restaurant.restaurant.name ?? restaurant.restaurant.slug ?? 'Unknown restaurant'
  const riskCount = restaurant.criticalOrders + restaurant.warningOrders
  const latestReason = restaurant.latestFailureReason ?? (
    restaurant.activeOrders > 0 ? 'Current orders look healthy.' : 'No active order activity.'
  )

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`min-h-[10rem] rounded-lg border p-3 text-left transition-colors ${restaurantHealthClass(restaurant, selected)}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-[var(--color-text-primary)]">{name}</p>
          <p className="mt-0.5 truncate text-xs text-[var(--color-text-muted)]">{restaurant.restaurant.slug ?? restaurant.restaurant.phone ?? 'no slug'}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-2xl font-semibold text-[var(--color-text-primary)]">{restaurant.activeOrders}</p>
          <p className="text-[0.65rem] uppercase tracking-wide text-[var(--color-text-muted)]">active</p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-4 gap-1.5 text-center">
        <div>
          <p className="text-sm font-semibold text-emerald-300">
            {restaurant.successPercent === null ? '-' : `${restaurant.successPercent}%`}
          </p>
          <p className="text-[0.6rem] uppercase tracking-wide text-[var(--color-text-muted)]">success</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-amber-300">{restaurant.requestOrders}</p>
          <p className="text-[0.6rem] uppercase tracking-wide text-[var(--color-text-muted)]">new</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-sky-300">{restaurant.unseenOrders}</p>
          <p className="text-[0.6rem] uppercase tracking-wide text-[var(--color-text-muted)]">unseen</p>
        </div>
        <div>
          <p className={`text-sm font-semibold ${riskCount > 0 ? 'text-red-300' : 'text-[var(--color-text-muted)]'}`}>{riskCount}</p>
          <p className="text-[0.6rem] uppercase tracking-wide text-[var(--color-text-muted)]">risk</p>
        </div>
      </div>

      <div className="mt-3 space-y-1.5">
        {restaurant.currentOrders.length > 0 ? restaurant.currentOrders.slice(0, 3).map((order) => (
          <div key={order.orderDocumentId} className="flex items-center justify-between gap-2 text-xs">
            <span className={`shrink-0 rounded border px-1.5 py-0.5 uppercase ${severityClass(order.severity)}`}>{order.severity}</span>
            <span className="min-w-0 flex-1 truncate text-[var(--color-text-secondary)]">
              {order.guestName ?? 'Guest'} | {order.status ?? 'unknown'} | {order.itemCount} item{order.itemCount === 1 ? '' : 's'}
            </span>
          </div>
        )) : (
          <p className="text-xs text-[var(--color-text-muted)]">No active orders right now.</p>
        )}
      </div>

      <p className="mt-3 truncate text-xs text-[var(--color-text-muted)]" title={latestReason}>
        {formatMonitorTimestamp(restaurant.latestOrderAt)} | {latestReason}
      </p>
    </button>
  )
}

function StageCell({ title, stage }: { title: string; stage: OrderMonitorStage }) {
  return (
    <div className={`min-w-[8.5rem] rounded-md border px-2.5 py-2 ${stageClass(stage.status)}`} title={stage.message ?? stage.label}>
      <p className="text-[0.65rem] font-semibold uppercase tracking-wide opacity-80">{title}</p>
      <p className="mt-0.5 text-xs font-semibold truncate">{stage.label}</p>
      <p className="mt-0.5 text-[0.65rem] opacity-75 truncate">
        {stage.at ? new Date(stage.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : stage.eventType ?? stage.status}
      </p>
    </div>
  )
}

function OrderMonitorCard({ row }: { row: OrderMonitorRow }) {
  const [expanded, setExpanded] = useState(false)
  const stages = [
    ['Created', row.stages.orderCreated],
    ['Dispatch', row.stages.backendDispatch],
    ['Received', row.stages.deviceReceived],
    ['Seen', row.stages.restaurantSeen],
    ['Print', row.stages.print],
  ] as const

  return (
    <div className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-4 py-3 text-left hover:bg-white/[0.03]"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded border px-2 py-0.5 text-xs font-semibold uppercase ${severityClass(row.severity)}`}>
                {row.severity}
              </span>
              <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                {row.restaurant?.name ?? row.restaurant?.slug ?? 'Unknown restaurant'}
              </span>
              <span className="font-mono text-xs text-amber-400">{shortId(row.orderDocumentId)}</span>
              <span className="text-xs text-[var(--color-text-muted)]">{row.source ?? 'unknown source'}</span>
            </div>
            <p className="mt-1 text-xs text-[var(--color-text-muted)]">
              {new Date(row.createdAt).toLocaleString()} | {row.fulfillmentMethod ?? 'order'} | {row.status ?? 'unknown'} | age {row.ageMinutes}m
            </p>
            <p className="mt-1 text-xs text-[var(--color-text-primary)]">
              {row.guest.name ?? 'Guest'} {row.guest.phone ? `| ${row.guest.phone}` : ''} | {row.itemCount} item{row.itemCount === 1 ? '' : 's'}
            </p>
            <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{row.reason}</p>
          </div>
          {row.severity === 'critical' && <AlertTriangle className="h-4 w-4 text-red-300 shrink-0" />}
        </div>

        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2">
          {stages.map(([title, stage]) => (
            <StageCell key={title} title={title} stage={stage} />
          ))}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[var(--color-border-subtle)] p-4 space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 text-xs">
            <div className="rounded-md bg-[var(--color-bg-subtle)] border border-[var(--color-border-subtle)] p-2">
              <p className="text-[var(--color-text-muted)]">Customer</p>
              <p className="font-semibold text-[var(--color-text-primary)]">{row.guest.name ?? 'Unknown'}</p>
              <p className="font-mono text-[var(--color-text-secondary)] break-all">{row.guest.phone ?? 'No phone'}</p>
            </div>
            <div className="rounded-md bg-[var(--color-bg-subtle)] border border-[var(--color-border-subtle)] p-2">
              <p className="text-[var(--color-text-muted)]">Order</p>
              <p className="font-mono text-[var(--color-text-primary)] break-all">{row.orderDocumentId}</p>
              <p className="text-[var(--color-text-secondary)]">{row.bookosId ?? 'No bookos id'}</p>
            </div>
            <div className="rounded-md bg-[var(--color-bg-subtle)] border border-[var(--color-border-subtle)] p-2">
              <p className="text-[var(--color-text-muted)]">Items</p>
              <p className="text-[var(--color-text-primary)]">
                {row.items.length > 0 ? row.items.map((item) => `${item.quantity}x ${item.name}`).join(', ') : 'No items loaded'}
              </p>
            </div>
          </div>

          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Delivery Events ({row.events.length})</p>
            {row.events.length === 0 ? (
              <div className="rounded-md border border-dashed border-[var(--color-border-subtle)] p-3 text-xs text-[var(--color-text-muted)]">
                No Sunmi delivery events recorded for this order.
              </div>
            ) : (
              <div className="space-y-1.5">
                {row.events.map((event) => (
                  <div key={event.id} className="flex flex-wrap items-center gap-2 rounded-md bg-[var(--color-bg-subtle)] border border-[var(--color-border-subtle)] px-2 py-1.5 text-xs">
                    <span className={`font-semibold uppercase ${statusClass(event.ok)}`}>{statusLabel(event.ok)}</span>
                    <span className="font-semibold text-[var(--color-text-primary)]">{event.eventType}</span>
                    <span className="text-[var(--color-text-muted)]">{new Date(event.timestamp).toLocaleString()}</span>
                    <span className="text-[var(--color-text-muted)]">{event.deviceName ?? 'unknown device'}</span>
                    {event.message && <span className="text-[var(--color-text-secondary)] truncate">{event.message}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function OrderMonitorPanel() {
  const [search, setSearch] = useState('')
  const [severity, setSeverity] = useState<'all' | OrderMonitorRow['severity']>('all')
  const [selectedRestaurantId, setSelectedRestaurantId] = useState('')
  const { data, isLoading, refetch } = useAdminOrderMonitor({
    restaurantDocumentId: selectedRestaurantId || undefined,
    limit: 120,
  })

  const rows = data?.rows ?? []
  const restaurants = data?.restaurants ?? []
  const selectedRestaurant = restaurants.find((restaurant) => restaurant.restaurant.documentId === selectedRestaurantId)
  const activeTotals = restaurants.reduce(
    (totals, restaurant) => ({
      active: totals.active + restaurant.activeOrders,
      request: totals.request + restaurant.requestOrders,
      unseen: totals.unseen + restaurant.unseenOrders,
      risk: totals.risk + restaurant.warningOrders + restaurant.criticalOrders,
    }),
    { active: 0, request: 0, unseen: 0, risk: 0 },
  )
  const query = search.trim().toLowerCase()
  const filteredRows = rows.filter((row) => {
    const severityOk = severity === 'all' || row.severity === severity
    const haystack = [
      row.orderDocumentId,
      row.bookosId,
      row.restaurant?.name,
      row.restaurant?.slug,
      row.guest.name,
      row.guest.phone,
      row.source,
      row.status,
    ].filter(Boolean).join(' ').toLowerCase()
    return severityOk && (!query || haystack.includes(query))
  })

  return (
    <div className="flex flex-col h-full p-5 gap-4 overflow-y-auto">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Order Monitor</h2>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            Realtime order health by restaurant, with detailed workflow traces for dispatch, Sunmi receipt, restaurant visibility, and printing.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isLoading}>
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          ['Active orders', activeTotals.active, 'text-[var(--color-text-primary)]'],
          ['Waiting accept', activeTotals.request, 'text-amber-300'],
          ['Unseen', activeTotals.unseen, 'text-sky-300'],
          ['At risk', activeTotals.risk, 'text-red-300'],
        ].map(([label, value, color]) => (
          <div key={label} className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">{label}</p>
            <p className={`mt-1 text-2xl font-semibold ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Restaurant Heatmap</p>
            <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
              {selectedRestaurant
                ? `Showing workflow details for ${selectedRestaurant.restaurant.name ?? selectedRestaurant.restaurant.slug ?? 'selected restaurant'}.`
                : 'Showing all restaurants. Select one to inspect its current orders and traces.'}
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setSelectedRestaurantId('')}
            disabled={!selectedRestaurantId}
          >
            All restaurants
          </Button>
        </div>
        {isLoading && restaurants.length === 0 ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--color-text-muted)]" />
          </div>
        ) : restaurants.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[var(--color-border-subtle)] p-6 text-sm text-[var(--color-text-muted)]">
            No restaurants found for the platform monitor.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2">
            {restaurants.map((restaurant) => (
              <RestaurantHeatmapCard
                key={restaurant.restaurant.documentId}
                restaurant={restaurant}
                selected={restaurant.restaurant.documentId === selectedRestaurantId}
                onSelect={() => setSelectedRestaurantId(restaurant.restaurant.documentId)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_14rem_12rem] gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search restaurant, order id, customer phone, or source"
          className="px-3 py-2 text-sm rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-purple-500/60"
        />
        <select
          value={selectedRestaurantId}
          onChange={(e) => setSelectedRestaurantId(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] focus:outline-none"
        >
          <option value="">All restaurants</option>
          {restaurants.map((restaurant) => (
            <option key={restaurant.restaurant.documentId} value={restaurant.restaurant.documentId}>
              {restaurant.restaurant.name ?? restaurant.restaurant.slug ?? restaurant.restaurant.documentId}
            </option>
          ))}
        </select>
        <select
          value={severity}
          onChange={(e) => setSeverity(e.target.value as 'all' | OrderMonitorRow['severity'])}
          className="px-3 py-2 text-sm rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] focus:outline-none"
        >
          <option value="all">All severities</option>
          <option value="critical">Critical</option>
          <option value="warning">Warning</option>
          <option value="healthy">Healthy</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--color-text-muted)]" />
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[var(--color-border-subtle)] p-6 text-sm text-[var(--color-text-muted)]">
          No matching workflow traces.
        </div>
      ) : (
        <div className="space-y-2">
          {filteredRows.map((row) => (
            <OrderMonitorCard key={row.orderDocumentId} row={row} />
          ))}
        </div>
      )}
    </div>
  )
}

function SunmiLogCard({ log }: { log: SunmiDiagnosticLog }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="border border-[var(--color-border-subtle)] rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/[0.03]"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-xs font-semibold uppercase ${statusClass(log.ok)}`}>{statusLabel(log.ok)}</span>
            <span className="text-sm font-semibold text-[var(--color-text-primary)]">{log.eventType}</span>
            {log.orderDocumentId && (
              <span className="text-xs text-amber-400 font-mono">order {shortId(log.orderDocumentId)}</span>
            )}
            <span className="text-xs text-[var(--color-text-muted)]">{log.ipAddress ?? 'no ip'}</span>
          </div>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {new Date(log.timestamp).toLocaleString()} | {log.restaurantName ?? log.restaurantSlug ?? 'unknown restaurant'} | {log.deviceName ?? 'unknown device'} | {shortId(log.deviceId)}
          </p>
          {log.message && <p className="mt-1 text-xs text-[var(--color-text-secondary)] truncate">{log.message}</p>}
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
      </button>

      {expanded && (
        <div className="space-y-3 border-t border-[var(--color-border-subtle)] p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <div className="rounded-md bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] p-2">
              <p className="text-[var(--color-text-muted)]">Device</p>
              <p className="font-mono text-[var(--color-text-primary)] break-all">{log.deviceId ?? 'unknown'}</p>
              <p className="text-[var(--color-text-secondary)]">{log.deviceName ?? 'Unknown device'} | {log.platform ?? 'unknown'} | app {log.appVersion ?? 'unknown'}</p>
            </div>
            <div className="rounded-md bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] p-2">
              <p className="text-[var(--color-text-muted)]">Network</p>
              <p className="font-mono text-[var(--color-text-primary)]">{log.ipAddress ?? 'unknown ip'}</p>
              <p className="text-[var(--color-text-secondary)] truncate">{log.userAgent ?? 'unknown user agent'}</p>
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Details</p>
            <JsonBlock value={log.details} />
          </div>
        </div>
      )}
    </div>
  )
}

function SunmiDiagnosisPanel() {
  const { data: logs = [], isLoading, refetch } = useAdminSunmiDiagnostics()
  const clear = useAdminClearSunmiDiagnostics()
  const [orderFilter, setOrderFilter] = useState('')
  const [deviceFilter, setDeviceFilter] = useState('')

  const filteredLogs = logs.filter((log) => {
    const orderOk = !orderFilter.trim() || (log.orderDocumentId ?? '').toLowerCase().includes(orderFilter.trim().toLowerCase())
    const deviceHaystack = `${log.deviceId ?? ''} ${log.deviceName ?? ''} ${log.ipAddress ?? ''}`.toLowerCase()
    const deviceOk = !deviceFilter.trim() || deviceHaystack.includes(deviceFilter.trim().toLowerCase())
    return orderOk && deviceOk
  })

  const devices = Array.from(
    logs.reduce((map, log) => {
      const key = log.deviceId ?? `unknown:${log.deviceName ?? log.ipAddress ?? 'device'}`
      const current = map.get(key) ?? {
        key,
        deviceId: log.deviceId,
        deviceName: log.deviceName,
        restaurantName: log.restaurantName,
        ipAddress: log.ipAddress,
        lastSeen: log.timestamp,
        events: 0,
        failures: 0,
      }
      current.events += 1
      if (log.ok === false) current.failures += 1
      if (new Date(log.timestamp).getTime() > new Date(current.lastSeen).getTime()) {
        current.lastSeen = log.timestamp
        current.deviceName = log.deviceName
        current.restaurantName = log.restaurantName
        current.ipAddress = log.ipAddress
      }
      map.set(key, current)
      return map
    }, new Map<string, {
      key: string
      deviceId: string | null
      deviceName: string | null
      restaurantName: string | null
      ipAddress: string | null
      lastSeen: string
      events: number
      failures: number
    }>())
    .values(),
  ).sort((a, b) => new Date(b.lastSeen).getTime() - new Date(a.lastSeen).getTime())

  async function clearLogs() {
    try {
      await clear.mutateAsync()
      toast.success('Sunmi diagnostics cleared')
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to clear diagnostics')
    }
  }

  return (
    <div className="flex flex-col h-full p-5 gap-4 overflow-y-auto">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Sunmi Diagnosis</h2>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            Tracks backend dispatch, push token registration, WebSocket status, order fetches, device IPs, and alert attempts per Sunmi device.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isLoading}>
            Refresh
          </Button>
          <Button variant="secondary" size="sm" onClick={clearLogs} disabled={clear.isPending || logs.length === 0}>
            Clear
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {devices.length === 0 ? (
          <div className="lg:col-span-3 rounded-lg border border-dashed border-[var(--color-border-subtle)] p-4 text-sm text-[var(--color-text-muted)]">
            No Sunmi devices have reported yet. Open the receiver app on a Sunmi device and create a test order.
          </div>
        ) : (
          devices.slice(0, 6).map((device) => (
            <div key={device.key} className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-[var(--color-text-primary)] truncate">{device.deviceName ?? 'Unknown device'}</p>
                <span className={`text-xs font-semibold ${device.failures > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                  {device.failures} fail
                </span>
              </div>
              <p className="mt-1 text-xs text-[var(--color-text-muted)] truncate">{device.restaurantName ?? 'Unknown restaurant'}</p>
              <p className="mt-1 text-xs font-mono text-[var(--color-text-secondary)]">{shortId(device.deviceId)}</p>
              <p className="mt-1 text-xs text-[var(--color-text-muted)]">IP {device.ipAddress ?? 'unknown'} | {device.events} events</p>
              <p className="mt-1 text-xs text-[var(--color-text-muted)]">Last seen {new Date(device.lastSeen).toLocaleString()}</p>
            </div>
          ))
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          value={orderFilter}
          onChange={(e) => setOrderFilter(e.target.value)}
          placeholder="Filter by order documentId"
          className="px-3 py-2 text-sm rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-purple-500/60"
        />
        <input
          value={deviceFilter}
          onChange={(e) => setDeviceFilter(e.target.value)}
          placeholder="Filter by device name, device id, or IP"
          className="px-3 py-2 text-sm rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-purple-500/60"
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--color-text-muted)]" />
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[var(--color-border-subtle)] p-6 text-sm text-[var(--color-text-muted)]">
          No matching Sunmi diagnostic events.
        </div>
      ) : (
        <div className="space-y-2">
          {filteredLogs.map((log) => (
            <SunmiLogCard key={log.id} log={log} />
          ))}
        </div>
      )}
    </div>
  )
}

function TwilioSmsLogCard({ log }: { log: TwilioSmsDiagnosticLog }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="border border-[var(--color-border-subtle)] rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/[0.03]"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-xs font-semibold uppercase ${statusClass(log.ok)}`}>{statusLabel(log.ok)}</span>
            <span className="text-sm font-semibold text-[var(--color-text-primary)]">{log.eventType}</span>
            {log.orderDocumentId && (
              <span className="text-xs text-amber-400 font-mono">order {shortId(log.orderDocumentId)}</span>
            )}
            {log.recipient && <span className="text-xs text-[var(--color-text-muted)]">{log.recipient}</span>}
          </div>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {new Date(log.timestamp).toLocaleString()} | {log.restaurantName ?? log.restaurantSlug ?? 'unknown restaurant'} | {log.originChannel ?? log.source ?? 'unknown source'}
          </p>
          {log.message && <p className="mt-1 text-xs text-[var(--color-text-secondary)] truncate">{log.message}</p>}
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
      </button>

      {expanded && (
        <div className="space-y-3 border-t border-[var(--color-border-subtle)] p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
            <div className="rounded-md bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] p-2">
              <p className="text-[var(--color-text-muted)]">Recipient</p>
              <p className="font-mono text-[var(--color-text-primary)] break-all">{log.recipient ?? 'unknown'}</p>
              <p className="text-[var(--color-text-secondary)]">From {log.from ?? 'env default'}</p>
            </div>
            <div className="rounded-md bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] p-2">
              <p className="text-[var(--color-text-muted)]">Twilio</p>
              <p className="font-mono text-[var(--color-text-primary)] break-all">{log.messageSid ?? 'no sid'}</p>
              <p className="text-[var(--color-text-secondary)]">{log.fulfillmentMethod ?? 'unknown method'}</p>
            </div>
            <div className="rounded-md bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] p-2">
              <p className="text-[var(--color-text-muted)]">Source</p>
              <p className="text-[var(--color-text-primary)]">{log.originChannel ?? log.source ?? 'unknown'}</p>
              <p className="font-mono text-[var(--color-text-secondary)]">{shortId(log.restaurantDocumentId)}</p>
            </div>
          </div>
          {log.body && (
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Message Body</p>
              <div className="rounded-md bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] p-3 text-sm text-[var(--color-text-primary)] whitespace-pre-wrap">
                {log.body}
              </div>
            </div>
          )}
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Details</p>
            <JsonBlock value={log.details} />
          </div>
        </div>
      )}
    </div>
  )
}

function normalizeSmsSenderName(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9 +\-_&]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function suggestSmsSenderName(name?: string | null, slug?: string | null) {
  const base = normalizeSmsSenderName(name ?? '') || normalizeSmsSenderName(slug ?? '')
  if (!base) return ''
  if (base.length <= 11) return base
  const compact = base.replace(/\s+/g, '')
  return (compact || base).slice(0, 11)
}

function getSmsSenderError(value: string) {
  const trimmed = value.trim()
  if (!trimmed) return null
  if (/^\+?[\d\s().-]{6,20}$/.test(trimmed)) return null
  if (!/[A-Za-z]/.test(trimmed)) return 'Sender ID must include at least one letter.'
  if (trimmed.length < 3 || trimmed.length > 11) return 'Sender ID must be 3-11 characters.'
  if (!/^[A-Za-z0-9 +\-_&]+$/.test(trimmed)) {
    return 'Use only letters, numbers, spaces, +, -, _, or &.'
  }
  return null
}

function TwilioSmsPanel() {
  const { data: logs = [], isLoading, refetch } = useAdminTwilioSmsDiagnostics()
  const { data: settings, isLoading: settingsLoading, error: settingsError } = useAdminReadyTimeMessaging()
  const clear = useAdminClearTwilioSmsDiagnostics()
  const update = useAdminUpdateReadyTimeMessaging()
  const testSms = useAdminSendReadyTimeSmsTest()
  const [selectedRestaurantId, setSelectedRestaurantId] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [smsFrom, setSmsFrom] = useState('')
  const [whatsappNumbers, setWhatsappNumbers] = useState('')
  const [template, setTemplate] = useState('')
  const [orderFilter, setOrderFilter] = useState('')
  const [recipientFilter, setRecipientFilter] = useState('')
  const [testTo, setTestTo] = useState('')
  const [testMinutes, setTestMinutes] = useState(20)
  const [testMethod, setTestMethod] = useState<'pickup' | 'delivery'>('pickup')

  const selectedRestaurant = settings?.restaurants.find((restaurant) => restaurant.documentId === selectedRestaurantId)
  const suggestedSmsSender = suggestSmsSenderName(selectedRestaurant?.name, selectedRestaurant?.slug)
  const smsSenderError = getSmsSenderError(smsFrom)

  useEffect(() => {
    if (!settings?.restaurants.length) return
    setSelectedRestaurantId((current) => current || settings.restaurants[0].documentId)
  }, [settings])

  useEffect(() => {
    if (!selectedRestaurant) return
    setEnabled(selectedRestaurant.readyTimeSmsEnabled)
    setSmsFrom(selectedRestaurant.readyTimeSmsFromNumber ?? '')
    setWhatsappNumbers((selectedRestaurant.readyTimeWhatsappFromNumbers ?? []).join('\n'))
    setTemplate(selectedRestaurant.readyTimeMessageTemplate ?? '')
  }, [selectedRestaurant])

  const smsCapableNumbers = (settings?.twilioNumbers ?? []).filter((number) => number.capabilities?.sms === true)

  const filteredLogs = logs.filter((log) => {
    const restaurantOk = !selectedRestaurantId || log.restaurantDocumentId === selectedRestaurantId
    const orderOk = !orderFilter.trim() || (log.orderDocumentId ?? '').toLowerCase().includes(orderFilter.trim().toLowerCase())
    const recipientOk = !recipientFilter.trim() || (log.recipient ?? '').toLowerCase().includes(recipientFilter.trim().toLowerCase())
    return restaurantOk && orderOk && recipientOk
  })

  async function saveSettings() {
    if (!selectedRestaurantId) return
    if (smsSenderError) {
      toast.error(smsSenderError)
      return
    }
    try {
      await update.mutateAsync({
        restaurantDocumentId: selectedRestaurantId,
        readyTimeSmsEnabled: enabled,
        readyTimeSmsFromNumber: smsFrom,
        readyTimeWhatsappFromNumbers: whatsappNumbers.split('\n').map((value) => value.trim()).filter(Boolean),
        readyTimeMessageTemplate: template,
      })
      toast.success('Ready-time messaging saved')
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to save messaging settings')
    }
  }

  async function sendTest() {
    if (!selectedRestaurantId) return
    try {
      const result = await testSms.mutateAsync({
        restaurantDocumentId: selectedRestaurantId,
        to: testTo,
        estimatedReadyMinutes: testMinutes,
        fulfillmentMethod: testMethod,
      })
      toast.success(result.sid ? `Test SMS queued: ${result.sid}` : 'Test SMS sent')
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to send test SMS')
    }
  }

  async function clearLogs() {
    try {
      await clear.mutateAsync()
      toast.success('Twilio SMS diagnostics cleared')
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to clear diagnostics')
    }
  }

  return (
    <div className="flex flex-col h-full p-5 gap-4 overflow-y-auto">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Twilio SMS</h2>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            Tracks ready-time SMS attempts, skips, failures, Twilio SIDs, recipients, senders, and order sources.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isLoading}>
            Refresh
          </Button>
          <Button variant="secondary" size="sm" onClick={clearLogs} disabled={clear.isPending || logs.length === 0}>
            Clear
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-4 space-y-3">
        {settingsError && (
          <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
            {(settingsError as any)?.message ?? 'Failed to load ready-time messaging settings.'}
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <label className="space-y-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Restaurant</span>
            <select
              value={selectedRestaurantId}
              onChange={(e) => setSelectedRestaurantId(e.target.value)}
              disabled={settingsLoading}
              className="w-full px-3 py-2 text-sm rounded-lg bg-[var(--color-bg-subtle)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] focus:outline-none"
            >
              {(settings?.restaurants ?? []).map((restaurant) => (
                <option key={restaurant.documentId} value={restaurant.documentId}>
                  {restaurant.name} ({restaurant.slug})
                </option>
              ))}
            </select>
            {!settingsLoading && !settingsError && (settings?.restaurants ?? []).length === 0 && (
              <span className="text-xs text-red-300">No restaurants returned by the platform settings endpoint.</span>
            )}
          </label>
          <label className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Customer SMS sender</span>
              {suggestedSmsSender && (
                <button
                  type="button"
                  onClick={() => setSmsFrom(suggestedSmsSender)}
                  className="text-xs font-medium text-purple-400 hover:text-purple-300"
                >
                  Use {suggestedSmsSender}
                </button>
              )}
            </div>
            <input
              value={smsFrom}
              onChange={(e) => setSmsFrom(e.target.value)}
              list="ready-time-sms-senders"
              placeholder="Use backend default, +15551234567, or DaMimmo"
              className={`w-full px-3 py-2 text-sm rounded-lg bg-[var(--color-bg-subtle)] border text-[var(--color-text-primary)] focus:outline-none ${
                smsSenderError ? 'border-red-500/60' : 'border-[var(--color-border-subtle)]'
              }`}
            />
            <datalist id="ready-time-sms-senders">
              {smsCapableNumbers.map((number) => (
                <option key={number.twilio_phone_number_sid} value={number.phone_number}>
                  {number.phone_number}{number.label ? ` - ${number.label}` : ''}
                </option>
              ))}
            </datalist>
            <p className={`text-xs ${smsSenderError ? 'text-red-300' : 'text-[var(--color-text-muted)]'}`}>
              {smsSenderError ?? 'Use a Twilio number or an approved alphanumeric Sender ID. Sender names are one-way SMS only and must be approved/supported by Twilio for the destination country.'}
            </p>
          </label>
          <label className="flex items-center gap-2 pt-6">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-[var(--color-border-subtle)]"
            />
            <span className="text-sm text-[var(--color-text-primary)]">Send ready-time SMS on approval</span>
          </label>
        </div>

        <label className="space-y-1.5 block">
          <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Message Template</span>
          <textarea
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            placeholder={settings?.defaultTemplate ?? 'Use default ready-time message'}
            rows={3}
            className="w-full px-3 py-2 text-sm rounded-lg bg-[var(--color-bg-subtle)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-purple-500/60 resize-none"
          />
          <p className="text-xs text-[var(--color-text-muted)]">
            Placeholders: {'{restaurant_name}'}, {'{customer_name}'}, {'{ready_time}'}, {'{ready_minutes}'}, {'{fulfillment_method}'}, {'{order_id}'}.
          </p>
        </label>

        <label className="space-y-1.5 block">
          <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Allowed WhatsApp Sender Numbers</span>
          <textarea
            value={whatsappNumbers}
            onChange={(e) => setWhatsappNumbers(e.target.value)}
            placeholder="+353...\n+44..."
            rows={2}
            className="w-full px-3 py-2 text-sm font-mono rounded-lg bg-[var(--color-bg-subtle)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-purple-500/60 resize-none"
          />
          <p className="text-xs text-[var(--color-text-muted)]">
            Store the WhatsApp-enabled Twilio sender numbers that this restaurant is allowed to use for ready-time messages.
          </p>
        </label>

        <div className="flex justify-end">
          <Button onClick={saveSettings} disabled={update.isPending || !selectedRestaurantId || !!smsSenderError} className="bg-purple-600 hover:bg-purple-500 text-white">
            {update.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)] mb-3">Send Test SMS</p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_140px_auto] gap-2">
          <input
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="Recipient phone number"
            className="px-3 py-2 text-sm rounded-lg bg-[var(--color-bg-subtle)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-purple-500/60"
          />
          <input
            type="number"
            min={0}
            max={240}
            value={testMinutes}
            onChange={(e) => setTestMinutes(Number(e.target.value))}
            className="px-3 py-2 text-sm rounded-lg bg-[var(--color-bg-subtle)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] focus:outline-none focus:border-purple-500/60"
          />
          <select
            value={testMethod}
            onChange={(e) => setTestMethod(e.target.value as 'pickup' | 'delivery')}
            className="px-3 py-2 text-sm rounded-lg bg-[var(--color-bg-subtle)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] focus:outline-none"
          >
            <option value="pickup">Collection</option>
            <option value="delivery">Delivery</option>
          </select>
          <Button onClick={sendTest} disabled={testSms.isPending || !selectedRestaurantId || !testTo.trim()} className="bg-purple-600 hover:bg-purple-500 text-white">
            {testSms.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Send
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          value={orderFilter}
          onChange={(e) => setOrderFilter(e.target.value)}
          placeholder="Filter by order documentId"
          className="px-3 py-2 text-sm rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-purple-500/60"
        />
        <input
          value={recipientFilter}
          onChange={(e) => setRecipientFilter(e.target.value)}
          placeholder="Filter by recipient phone"
          className="px-3 py-2 text-sm rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-purple-500/60"
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--color-text-muted)]" />
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[var(--color-border-subtle)] p-6 text-sm text-[var(--color-text-muted)]">
          No matching Twilio SMS diagnostic events.
        </div>
      ) : (
        <div className="space-y-2">
          {filteredLogs.map((log) => (
            <TwilioSmsLogCard key={log.id} log={log} />
          ))}
        </div>
      )}
    </div>
  )
}

function KnowledgeBaseTab({ agentId, kbDocs }: { agentId: string; kbDocs: AdminAgentKbDoc[] }) {
  const [localDocs, setLocalDocs] = useState<AdminAgentKbDoc[]>(kbDocs)
  const [showAdd, setShowAdd] = useState(false)
  const [newDocName, setNewDocName] = useState('')
  const [newDocText, setNewDocText] = useState('')
  const [addingDoc, setAddingDoc] = useState(false)
  const updateKb = useAdminUpdateKnowledgeBase(agentId)

  async function removeDoc(doc: AdminAgentKbDoc) {
    const updated = localDocs.filter((d) => d.id !== doc.id)
    try {
      await updateKb.mutateAsync(updated)
      setLocalDocs(updated)
      toast.success('Document removed')
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to remove document')
    }
  }

  async function addDoc() {
    if (!newDocName || !newDocText) { toast.error('Name and content are required'); return }
    setAddingDoc(true)
    try {
      const token = storage.getToken()
      const formData = new FormData()
      const blob = new Blob([newDocText], { type: 'text/plain' })
      formData.append('file', blob, `${newDocName}.txt`)
      formData.append('name', newDocName)
      const res = await fetch(`${STRAPI_URL}/api/she-answers/admin/agents/${agentId}/knowledge-base/add`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })
      if (!res.ok) throw new Error('Upload failed')
      const json = await res.json()
      const newDoc: AdminAgentKbDoc = { id: json.data?.id ?? json.id, name: newDocName, type: 'file' }
      const updated = [...localDocs, newDoc]
      await updateKb.mutateAsync(updated)
      setLocalDocs(updated)
      setNewDocName('')
      setNewDocText('')
      setShowAdd(false)
      toast.success('Document added')
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to add document')
    } finally {
      setAddingDoc(false)
    }
  }

  return (
    <div className="space-y-2">
      {localDocs.length === 0 && (
        <p className="text-sm text-[var(--color-text-muted)] py-4 text-center">No knowledge base documents attached.</p>
      )}
      {localDocs.map((doc) => (
        <div key={doc.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)]">
          <div className="flex items-center gap-2 min-w-0">
            <BookOpen className="h-4 w-4 text-purple-400 shrink-0" />
            <span className="text-sm text-[var(--color-text-primary)] truncate">{doc.name}</span>
            <span className="px-1.5 py-0.5 rounded text-[0.6rem] font-bold uppercase tracking-wide bg-[var(--color-bg-subtle)] text-[var(--color-text-muted)]">
              {doc.type}
            </span>
          </div>
          <button
            onClick={() => removeDoc(doc)}
            disabled={updateKb.isPending}
            className="p-1 rounded hover:bg-red-500/10 text-[var(--color-text-muted)] hover:text-red-400 transition-colors shrink-0"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}

      {showAdd ? (
        <div className="border border-purple-500/30 rounded-lg p-3 space-y-2 bg-purple-500/5">
          <input
            placeholder="Document name"
            value={newDocName}
            onChange={(e) => setNewDocName(e.target.value)}
            className="w-full px-2 py-1.5 text-sm rounded bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] focus:outline-none focus:border-purple-500/60"
          />
          <textarea
            placeholder="Paste document content here…"
            value={newDocText}
            onChange={(e) => setNewDocText(e.target.value)}
            rows={6}
            className="w-full px-2 py-1.5 text-sm rounded bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] focus:outline-none focus:border-purple-500/60 resize-none"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" size="sm" onClick={() => setShowAdd(false)}><X className="h-3.5 w-3.5" /></Button>
            <Button size="sm" className="bg-purple-600 hover:bg-purple-500 text-white" onClick={addDoc} disabled={addingDoc}>
              {addingDoc ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
              Upload
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="secondary" size="sm" onClick={() => setShowAdd(true)}>
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Document
        </Button>
      )}
    </div>
  )
}

// ── Phone Number Tab ───────────────────────────────────────────

function PhoneNumberTab({ agentId }: { agentId: string }) {
  const { data: phoneNumbers = [], isLoading } = usePlatformAdminPhoneNumbers()
  const assignPhone = useAdminAssignPhone()

  const assigned = phoneNumbers.find((p) => p.assigned_agent?.agent_id === agentId)

  async function handleAssign(phoneNumberId: string) {
    try {
      await assignPhone.mutateAsync({ phoneNumberId, agentId })
      toast.success('Phone number assigned')
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to assign')
    }
  }

  async function handleUnassign() {
    if (!assigned) return
    try {
      await assignPhone.mutateAsync({ phoneNumberId: assigned.phone_number_id, agentId: null })
      toast.success('Phone number unassigned')
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to unassign')
    }
  }

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[var(--color-text-muted)]" /></div>

  const free = phoneNumbers.filter((p) => !p.assigned_agent)

  return (
    <div className="space-y-4">
      <div className={`p-3 rounded-lg border ${assigned ? 'border-purple-500/30 bg-purple-500/5' : 'border-[var(--color-border-subtle)]'}`}>
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)] mb-1">Current Assignment</p>
        {assigned ? (
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-[var(--color-text-primary)] tracking-wide">{assigned.phone_number}</span>
            <Button variant="secondary" size="sm" onClick={handleUnassign} disabled={assignPhone.isPending} className="text-red-400 border-red-500/30 hover:border-red-500/50">
              Unassign
            </Button>
          </div>
        ) : (
          <p className="text-sm text-[var(--color-text-muted)]">No phone number assigned</p>
        )}
      </div>

      {free.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)] mb-2">Available Numbers</p>
          <div className="space-y-1.5">
            {free.map((p) => (
              <div key={p.phone_number_id} className="flex items-center justify-between p-2.5 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)]">
                <div>
                  <span className="text-sm font-medium text-[var(--color-text-primary)]">{p.phone_number}</span>
                  {p.label && <span className="ml-2 text-xs text-[var(--color-text-muted)]">{p.label}</span>}
                </div>
                <Button size="sm" variant="secondary" onClick={() => handleAssign(p.phone_number_id)} disabled={assignPhone.isPending}>
                  Assign
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)] mb-2">All Numbers</p>
        <div className="space-y-1">
          {phoneNumbers.map((p) => (
            <div key={p.phone_number_id} className="flex items-center justify-between px-2.5 py-1.5 rounded text-sm">
              <span className="text-[var(--color-text-primary)]">{p.phone_number}</span>
              <span className={`text-xs ${p.assigned_agent ? 'text-[var(--color-text-muted)]' : 'text-green-400'}`}>
                {p.assigned_agent ? p.assigned_agent.agent_name : 'Free'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Test Tab ───────────────────────────────────────────────────

type CallState = 'idle' | 'connecting' | 'connected' | 'error'
type AgentMode = 'listening' | 'speaking'

function SoundWave() {
  const heights = [4, 10, 7, 12, 5, 9, 4]
  return (
    <div className="flex items-center gap-px h-4">
      {heights.map((h, i) => (
        <motion.div
          key={i}
          className="w-0.5 rounded-full bg-purple-400"
          animate={{ height: [h * 0.5, h, h * 0.5] }}
          transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.07, ease: 'easeInOut' }}
          style={{ height: h * 0.5 }}
        />
      ))}
    </div>
  )
}

function TestTab({ agentId }: { agentId: string }) {
  const [callState, setCallState] = useState<CallState>('idle')
  const [agentMode, setAgentMode] = useState<AgentMode>('listening')
  const [transcript, setTranscript] = useState<{ role: 'agent' | 'user'; text: string }[]>([])
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const convRef = useRef<Awaited<ReturnType<typeof Conversation.startSession>> | null>(null)

  const startCall = useCallback(async () => {
    setCallState('connecting')
    setErrorMsg(null)
    setTranscript([])
    try {
      const token = storage.getToken()
      if (!token) throw new Error('Not authenticated')
      const wsUrl = `${STRAPI_URL.replace(/^http/, 'ws')}/api/she-answers/admin/ws/${agentId}?token=${token}`

      const conv = await Conversation.startSession({
        signedUrl: wsUrl,
        onConnect: () => setCallState('connected'),
        onDisconnect: () => { setCallState('idle'); convRef.current = null },
        onModeChange: ({ mode }) => setAgentMode(mode as AgentMode),
        onMessage: ({ message, source }: { message: string; source: 'user' | 'ai' }) => {
          setTranscript((prev) => [...prev, { role: source === 'ai' ? 'agent' : 'user', text: message }])
        },
        onError: (msg: string) => {
          setErrorMsg(msg)
          setCallState('error')
        },
      })
      convRef.current = conv
    } catch (err: any) {
      setErrorMsg(err?.message ?? 'Failed to start session')
      setCallState('error')
    }
  }, [agentId])

  const stopCall = useCallback(async () => {
    await convRef.current?.endSession()
    convRef.current = null
    setCallState('idle')
  }, [])

  return (
    <div className="space-y-4">
      <p className="text-xs text-[var(--color-text-muted)]">
        Talk directly to this agent from the browser — no phone call needed.
      </p>

      <div className="flex items-center gap-3">
        {callState === 'idle' || callState === 'error' ? (
          <button
            onClick={startCall}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium transition-colors"
          >
            <Mic className="h-3.5 w-3.5" />
            Start Test Call
          </button>
        ) : callState === 'connecting' ? (
          <button disabled className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-purple-600/60 text-white text-sm font-medium">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Connecting…
          </button>
        ) : (
          <>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20">
              {agentMode === 'speaking' ? (
                <>
                  <SoundWave />
                  <span className="text-xs text-purple-400 font-medium">Agent speaking</span>
                </>
              ) : (
                <>
                  <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-xs text-green-400 font-medium">Listening</span>
                </>
              )}
            </div>
            <button
              onClick={stopCall}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-500/30 hover:border-red-500/50 text-red-400 text-sm transition-colors"
            >
              <MicOff className="h-3.5 w-3.5" />
              End Call
            </button>
          </>
        )}
      </div>

      {errorMsg && <p className="text-xs text-red-400">{errorMsg}</p>}

      {transcript.length > 0 && (
        <div className="space-y-2 max-h-64 overflow-y-auto pt-1">
          {transcript.map((msg, i) => (
            <div key={i} className={`flex gap-2 ${msg.role !== 'agent' ? 'flex-row-reverse' : ''}`}>
              <div className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${
                msg.role === 'agent' ? 'bg-purple-500/20' : 'bg-[var(--color-bg-elevated)]'
              }`}>
                {msg.role === 'agent'
                  ? <Bot className="h-3.5 w-3.5 text-purple-400" />
                  : <User className="h-3.5 w-3.5 text-[var(--color-text-muted)]" />
                }
              </div>
              <div className={`max-w-[80%] rounded-lg px-3 py-1.5 text-xs ${
                msg.role === 'agent'
                  ? 'bg-purple-500/10 text-[var(--color-text-primary)]'
                  : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]'
              }`}>
                {msg.text}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Detail Panel ───────────────────────────────────────────────

const TABS = [
  { id: 'prompt', label: 'Prompt', icon: Bot },
  { id: 'tools', label: 'Tools', icon: Wrench },
  { id: 'kb', label: 'Knowledge Base', icon: BookOpen },
  { id: 'phone', label: 'Phone Number', icon: Phone },
  { id: 'test', label: 'Test', icon: Headphones },
  { id: 'debug', label: 'Debug', icon: Activity },
] as const

type TabId = (typeof TABS)[number]['id']

function AgentDetail({ agentId }: { agentId: string }) {
  const [activeTab, setActiveTab] = useState<TabId>('prompt')
  const { data: agent, isLoading } = usePlatformAdminAgent(agentId)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--color-text-muted)]" />
      </div>
    )
  }

  if (!agent) return null

  const cfg = agent.conversation_config.agent

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-5 pb-3 border-b border-[var(--color-border-subtle)]">
        <h2 className="text-base font-semibold text-[var(--color-text-primary)]">{agent.name}</h2>
        <p className="text-xs text-[var(--color-text-muted)] mt-0.5 font-mono">{agent.agent_id}</p>
      </div>

      <div className="flex gap-0 border-b border-[var(--color-border-subtle)] px-5 overflow-x-auto">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === id
                ? 'border-purple-500 text-purple-400'
                : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        {activeTab === 'prompt' && <PromptTab agentId={agentId} prompt={cfg.prompt.prompt} />}
        {activeTab === 'tools' && <ToolsTab agentId={agentId} tools={cfg.prompt.tools ?? []} />}
        {activeTab === 'kb' && <KnowledgeBaseTab agentId={agentId} kbDocs={cfg.prompt.knowledge_base ?? []} />}
        {activeTab === 'phone' && <PhoneNumberTab agentId={agentId} />}
        {activeTab === 'test' && <TestTab agentId={agentId} />}
        {activeTab === 'debug' && <DebugLogsTab agentId={agentId} />}
      </div>
    </div>
  )
}

// ── Main Page ──────────────────────────────────────────────────

export default function PlatformAdminPage() {
  const { data: agents = [], isLoading: isLoadingAgents } = usePlatformAdminAgents()
  const { data: restaurantAgents = [] } = useAdminRestaurantAgents()
  const { data: phoneNumbers = [] } = usePlatformAdminPhoneNumbers()
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null)
  const [showGlobalConfig, setShowGlobalConfig] = useState(false)
  const [showOrderMonitor, setShowOrderMonitor] = useState(false)
  const [showSunmiDiagnosis, setShowSunmiDiagnosis] = useState(false)
  const [showTwilioSms, setShowTwilioSms] = useState(false)

  const saAgentIds = new Set(restaurantAgents.map((r) => r.agentId))

  const phoneMap = phoneNumbers
    .filter((p) => p.assigned_agent)
    .map((p) => ({ agent_id: p.assigned_agent!.agent_id, phone_number: p.phone_number }))

  function selectAgent(id: string) {
    setSelectedAgentId(id)
    setShowGlobalConfig(false)
    setShowOrderMonitor(false)
    setShowSunmiDiagnosis(false)
    setShowTwilioSms(false)
  }

  function selectGlobalConfig() {
    setShowGlobalConfig(true)
    setShowOrderMonitor(false)
    setShowSunmiDiagnosis(false)
    setShowTwilioSms(false)
    setSelectedAgentId(null)
  }

  function selectOrderMonitor() {
    setShowOrderMonitor(true)
    setShowGlobalConfig(false)
    setShowSunmiDiagnosis(false)
    setShowTwilioSms(false)
    setSelectedAgentId(null)
  }

  function selectSunmiDiagnosis() {
    setShowSunmiDiagnosis(true)
    setShowGlobalConfig(false)
    setShowOrderMonitor(false)
    setShowTwilioSms(false)
    setSelectedAgentId(null)
  }

  function selectTwilioSms() {
    setShowTwilioSms(true)
    setShowSunmiDiagnosis(false)
    setShowGlobalConfig(false)
    setShowOrderMonitor(false)
    setSelectedAgentId(null)
  }

  return (
    <div className="flex flex-col h-full -m-4 sm:-m-6">
      <div className="px-6 pt-6 pb-0">
        <PageHeader title="Platform Admin" description="Manage ElevenLabs agents, prompts, tools and phone numbers" />
      </div>

      <div className="flex flex-1 overflow-hidden mt-4 border-t border-[var(--color-border-subtle)]">
        {/* Left — Agents list */}
        <div className="w-72 shrink-0 border-r border-[var(--color-border-subtle)] flex flex-col overflow-hidden">
          {isLoadingAgents ? (
            <div className="flex items-center justify-center flex-1">
              <Loader2 className="h-5 w-5 animate-spin text-[var(--color-text-muted)]" />
            </div>
          ) : (
            <AgentList
              agents={agents}
              saAgentIds={saAgentIds}
              restaurantAgents={restaurantAgents}
              selectedId={selectedAgentId}
              onSelect={selectAgent}
              onSelectGlobalConfig={selectGlobalConfig}
              onSelectOrderMonitor={selectOrderMonitor}
              onSelectSunmiDiagnosis={selectSunmiDiagnosis}
              onSelectTwilioSms={selectTwilioSms}
              showGlobalConfig={showGlobalConfig}
              showOrderMonitor={showOrderMonitor}
              showSunmiDiagnosis={showSunmiDiagnosis}
              showTwilioSms={showTwilioSms}
              phoneNumbers={phoneMap}
            />
          )}
        </div>

        {/* Right — Detail panel */}
        <div className="flex-1 overflow-hidden">
          {showGlobalConfig ? (
            <GlobalConfigPanel />
          ) : showOrderMonitor ? (
            <OrderMonitorPanel />
          ) : showSunmiDiagnosis ? (
            <SunmiDiagnosisPanel />
          ) : showTwilioSms ? (
            <TwilioSmsPanel />
          ) : selectedAgentId ? (
            <AgentDetail key={selectedAgentId} agentId={selectedAgentId} />
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-8">
              <Bot className="h-10 w-10 text-[var(--color-text-muted)] opacity-30" />
              <p className="text-sm text-[var(--color-text-muted)]">
                Select an agent from the list to view and edit its configuration, open Global Config, inspect order delivery, review Sunmi Diagnosis, or debug Twilio SMS.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
